import type { EpocusExam } from "$global-providers/rxdb-provider/collections/epocus-exam";
import type { ExamType } from "$global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import { NORMAL_WORKSHEET_BY_TYPE } from "$global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import type { ImagingInstance } from "$global-providers/rxdb-provider/collections/imaging-instance";
import { nanoid } from "nanoid";
import { useEffect } from "react";
import type { RxCollection } from "rxdb";
import { GE_PRESET_TO_EXAM_TYPE } from "../../../_lib/dicom-preset-mapping";
import { imagingInstanceToExamImage } from "../../../_lib/hooks/use-encounter-imaging-instances";

/**
 * PNE-213: Watches imaging instances for an encounter and auto-assigns those
 * with unambiguous DICOM presets to their matching exam worksheet type.
 *
 * - TTE   → cardiac
 * - CHEST → lung
 * - VASCULAR → aorta
 *
 * Creates the exam if one doesn't already exist for this encounter; otherwise
 * patches its images array with any new instances (deduplicated by id).
 * Instances with ambiguous or unknown presets are left unassigned.
 *
 * Note: the findOne→insert sequence is not strictly atomic. In the rare case
 * where two concurrent effect runs both observe !existing and both attempt an
 * insert, the second will throw a conflict error which is caught and ignored —
 * the first insert wins and no data is lost.
 */
export function useAutoAssignDicomImages({
  encounterId,
  instances,
  examCollection,
  userId,
}: {
  encounterId: string | null | undefined;
  instances: ReadonlyArray<ImagingInstance>;
  examCollection: RxCollection<EpocusExam> | null;
  userId: string;
}): void {
  useEffect(() => {
    if (!encounterId || instances.length === 0) return;
    const collection = examCollection;
    if (!collection) return;

    // Group instances by their unambiguous exam type
    const grouped = new Map<ExamType, ImagingInstance[]>();
    for (const inst of instances) {
      const preset = inst.dicomPreset;
      if (!preset) continue;
      const examType = GE_PRESET_TO_EXAM_TYPE[preset];
      if (!examType) continue; // null (ambiguous) or undefined (unknown preset)
      const list = grouped.get(examType) ?? [];
      list.push(inst);
      grouped.set(examType, list);
    }

    if (grouped.size === 0) return;

    let cancelled = false;

    (async () => {
      try {
        for (const [examType, insts] of grouped) {
          if (cancelled) break;

          const existing = await collection
            .findOne({ selector: { encounterId, examType } })
            .exec();

          if (cancelled) break;

          const newImages = insts.map(imagingInstanceToExamImage);

          if (!existing) {
            const normalData = NORMAL_WORKSHEET_BY_TYPE[examType];
            const emptyViews = Object.fromEntries(
              Object.keys(normalData.views).map((k) => [k, false]),
            );
            const now = new Date().toISOString();
            try {
              await collection.insert({
                id: nanoid(),
                encounterId,
                signedAt: null,
                signedByUserId: null,
                nonBillable: false,
                nonBillableReason: null,
                amendments: [],
                examType,
                status: "draft",
                worksheetData: JSON.parse(
                  JSON.stringify({
                    ...normalData,
                    indication: null,
                    views: emptyViews,
                  }),
                ) as EpocusExam["worksheetData"],
                images: newImages,
                meta: {
                  createdAt: now,
                  createdByUserId: userId,
                  updatedAt: now,
                  updatedByUserId: userId,
                },
              });
            } catch {
              // Concurrent insert won the race — the exam already exists.
              // Re-fetch and merge images so we don't silently drop them.
              if (cancelled) return;
              const created = await collection
                .findOne({ selector: { encounterId, examType } })
                .exec();
              if (created) {
                await created.incrementalModify((docData) => {
                  const currentIds = new Set(
                    docData.images.map((img) => img.id),
                  );
                  const additions = newImages.filter(
                    (img) => !currentIds.has(img.id),
                  );
                  if (additions.length === 0) return docData;
                  return {
                    ...docData,
                    images: [...docData.images, ...additions],
                  };
                });
              }
            }
          } else {
            // Use incrementalModify for an atomic read-modify-write so concurrent
            // writes don't clobber each other's images.
            await existing.incrementalModify((docData) => {
              const currentIds = new Set(docData.images.map((img) => img.id));
              const additions = newImages.filter(
                (img) => !currentIds.has(img.id),
              );
              if (additions.length === 0) return docData;
              return { ...docData, images: [...docData.images, ...additions] };
            });
          }
        }
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to auto-assign DICOM images", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [encounterId, instances, userId, examCollection]);
}
